import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { listProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ORGANIC_CATS } from "@/components/OrganicBadge";
import OrganicBanner from "@/components/OrganicBanner";
import { Button } from "@/components/ui/button";
import { ArrowRight, Truck, Wallet, ShieldCheck, Sprout, Leaf, Wrench, Sparkles, Bug, Apple, ShieldCheck as Shield } from "lucide-react";

const CATEGORY_ICONS = {
  pesticides: Sparkles,
  herbicides: Leaf,
  fertilizers: Sprout,
  seeds: Sprout,
  tools: Wrench,
  organic_pesticides: Bug,
  organic_produce: Apple,
  residue_free: Shield,
};

const HomePage = () => {
  const { t, lang } = useApp();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    listProducts()
      .then((data) => setProducts(data.slice(0, 8)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 leaf-pattern opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 relative">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFEBE2] border border-[#E2E8F0] mb-5">
                <span className="w-2 h-2 rounded-full bg-[#D97706]" />
                <span className="text-xs font-semibold text-[#1E293B] uppercase tracking-wider">
                  {t("trustedBy")}
                </span>
              </div>
              <h1
                className={`font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#1E293B] ${lang === "hi" ? "font-devanagari" : ""}`}
                data-testid="hero-title"
              >
                {t("heroTitle")}
              </h1>
              <p className={`mt-5 text-base sm:text-lg text-slate-600 max-w-lg ${lang === "hi" ? "font-devanagari" : ""}`}>
                {t("heroSubtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate("/products")}
                  data-testid="hero-shop-btn"
                  className="rounded-full h-12 px-6 bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold pressable"
                >
                  {t("shopNow")}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById("categories")?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="hero-browse-btn"
                  className="rounded-full h-12 px-6 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EFEBE2] font-semibold"
                >
                  {t("browseCategories")}
                </Button>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
                <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-[#2D5A27]" /> {t("freeDelivery")}</span>
                <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-[#2D5A27]" /> {t("codAvailable")}</span>
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#2D5A27]" /> {t("trustedBy")}</span>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#D97706]/20 blur-2xl" />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-[#2D5A27]/20 blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-xl">
                <img
                  src="https://images.pexels.com/photos/29039798/pexels-photo-29039798.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Farmer in field"
                  className="w-full h-[380px] sm:h-[460px] object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 border border-[#E2E8F0]">
                  <p className="text-xs uppercase tracking-widest text-[#D97706] font-bold">100+</p>
                  <p className="font-heading font-semibold text-[#1E293B] mt-0.5">Products in stock</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs font-semibold text-[#D97706] uppercase tracking-widest mb-1">
              {t("browseCategories")}
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1E293B]">
              {t("products")}
            </h2>
          </div>
          <Link to="/products" className="text-sm font-semibold text-[#2D5A27] hover:text-[#21421D] flex items-center gap-1" data-testid="view-all-link">
            {t("viewDetails")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {["pesticides", "herbicides", "fertilizers", "seeds", "tools", "organic_pesticides", "organic_produce", "residue_free"].map((cat) => {
            const Icon = CATEGORY_ICONS[cat];
            return (
              <Link
                key={cat}
                to={`/products?category=${cat}`}
                data-testid={`category-tile-${cat}`}
                className={`group relative border rounded-xl p-4 sm:p-5 hover-lift ${ORGANIC_CATS.includes(cat) ? "bg-[#F0FDF4] border-[#86EFAC]" : "bg-white border-[#E2E8F0]"}`}
              >
                {ORGANIC_CATS.includes(cat) && (
                  <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider bg-[#15803D] text-white rounded-full px-2 py-0.5">{lang === "hi" ? "नया" : "New"}</span>
                )}
                <div className="w-12 h-12 rounded-xl bg-[#EFEBE2] flex items-center justify-center mb-3 group-hover:bg-[#2D5A27] transition-colors">
                  <Icon className="w-6 h-6 text-[#2D5A27] group-hover:text-white transition-colors" />
                </div>
                <p className="font-heading font-semibold text-sm sm:text-base text-[#1E293B]">
                  {t(cat)}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      <OrganicBanner />

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1E293B]">
            {lang === "hi" ? "लोकप्रिय उत्पाद" : "Popular Products"}
          </h2>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
                <div className="aspect-square bg-[#EFEBE2] animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-[#EFEBE2] rounded animate-pulse" />
                  <div className="h-4 bg-[#EFEBE2] rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="featured-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default HomePage;
